import { useState } from "react";
import { Navigate, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";

export default function CustomerLogin() {
  const { login, isLoggedIn } = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedReturn = params.get("return_to");
  const returnTo = requestedReturn?.startsWith("/") && !requestedReturn.startsWith("//")
    ? requestedReturn
    : window.sessionStorage.getItem("mysitecare-checkout-intent");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Calling navigate() directly during render (rather than returning a <Navigate>
  // element, or from inside a useEffect) is unreliable in this react-router version —
  // it logs "You should call navigate() in a useEffect" and the redirect can silently
  // not take effect. <Navigate> is the correct declarative way to redirect on render.
  if (isLoggedIn) {
    return <Navigate to={returnTo || "/account/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const session = await login(email, password);
      navigate(returnTo || session.redirect_to || "/account/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#F8FAFC] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2.5 text-[#10B981] mb-6">
          <ShieldCheck size={22} />
          <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#0F172A]">My Account</span>
        </div>
        <h1 className="text-2xl font-bold font-display text-[#0F172A]">Sign in</h1>
        <p className="mt-2 text-sm text-[#475569]">Access your website, plan and billing.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="account-login-form">
          <div>
            <label htmlFor="customer-login-email" className="text-xs font-medium uppercase tracking-wide text-[#475569]">Email</label>
            <input id="customer-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="customer-login-email-input"
              className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm outline-none focus:border-[#10B981]" />
          </div>
          <div>
            <label htmlFor="customer-login-password" className="text-xs font-medium uppercase tracking-wide text-[#475569]">Password</label>
            <div className="mt-1.5 relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input id="customer-login-password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-[#E2E8F0] pl-9 pr-12 py-2.5 text-sm outline-none focus:border-[#10B981]" data-testid="customer-login-password-input" />
              <button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="customer-login-password-visibility-toggle">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            <div className="mt-2 text-right"><Link to="/account/forgot-password" className="text-xs font-semibold text-[#10B981] hover:underline" data-testid="customer-login-forgot-password-link">Forgot password?</Link></div>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="customer-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="customer-login-submit-button"
            className="w-full rounded-xl bg-[#0F172A] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-[#475569]">
          New to MySiteCare? <Link to={`/register${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ""}`} className="font-semibold text-[#10B981] hover:underline" data-testid="customer-login-register-link">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
